import React, { useEffect, useState } from 'react'
import { Helmet } from 'react-helmet'
import { Link } from 'react-router-dom'
import Header from '../components/Header/header'
import SubscribeSection from '../components/Subscribe'
import FooterSection from '../components/Footer'
import firebase from '../api/firestore'

const db = firebase.firestore()
const googleProvider = new firebase.auth.GoogleAuthProvider()

const TRAVEL_STYLES = [
  'Adventure & Outdoors',
  'Budget Backpacker',
  'Luxury Traveler',
  'Cultural Explorer',
  'Eco & Sustainable',
  'Road Tripper',
  'Digital Nomad',
]

export default function EditProfilePage({ history }) {
  const [user, setUser] = useState(null)
  const [authLoading, setAuthLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [message, setMessage] = useState('')
  const [form, setForm] = useState({
    displayName: '',
    bio: '',
    favoriteDestination: '',
    dreamDestination: '',
    travelStyle: '',
    favoriteActivity: '',
    isAgent: false,
    businessName: '',
    businessWebsite: '',
    businessSpecialty: '',
    businessTagline: '',
    businessPhone: '',
  })

  useEffect(() => {
    const unsubscribe = firebase.auth().onAuthStateChanged((u) => {
      setUser(u)
      setAuthLoading(false)
      if (u) loadProfile(u.uid, u)
    })
    return unsubscribe
  }, [])

  const loadProfile = async (uid, u) => {
    try {
      const doc = await db.collection('users').doc(uid).get()
      if (doc.exists) {
        const data = doc.data()
        setForm((prev) => ({
          ...prev,
          displayName: data.displayName || u.displayName || '',
          bio: data.bio || '',
          favoriteDestination: data.favoriteDestination || '',
          dreamDestination: data.dreamDestination || '',
          travelStyle: data.travelStyle || '',
          favoriteActivity: data.favoriteActivity || '',
          isAgent: data.isAgent || false,
          businessName: data.businessName || '',
          businessWebsite: data.businessWebsite || '',
          businessSpecialty: data.businessSpecialty || '',
          businessTagline: data.businessTagline || '',
          businessPhone: data.businessPhone || '',
        }))
      } else {
        setForm((prev) => ({
          ...prev,
          displayName: u.displayName || '',
        }))
      }
    } catch (err) {
      console.error('Error loading profile:', err)
    }
  }

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target
    setForm((prev) => ({ ...prev, [name]: type === 'checkbox' ? checked : value }))
  }

  const handleSave = async (e) => {
    e.preventDefault()
    if (!user) return
    setSaving(true)
    setMessage('')
    try {
      await db.collection('users').doc(user.uid).set(
        {
          displayName: form.displayName.trim(),
          bio: form.bio.trim(),
          favoriteDestination: form.favoriteDestination.trim(),
          dreamDestination: form.dreamDestination.trim(),
          travelStyle: form.travelStyle,
          favoriteActivity: form.favoriteActivity.trim(),
          isAgent: form.isAgent,
          businessName: form.isAgent ? form.businessName.trim() : '',
          businessWebsite: form.isAgent ? form.businessWebsite.trim() : '',
          businessSpecialty: form.isAgent ? form.businessSpecialty.trim() : '',
          businessTagline: form.isAgent ? form.businessTagline.trim() : '',
          businessPhone: form.isAgent ? form.businessPhone.trim() : '',
          updatedAt: firebase.firestore.FieldValue.serverTimestamp(),
        },
        { merge: true }
      )
      setMessage('Profile saved!')
      setTimeout(() => history.push(`/user/${user.uid}`), 1200)
    } catch (err) {
      console.error('Error saving profile:', err)
      setMessage('Something went wrong. Please try again.')
    }
    setSaving(false)
  }

  const signInWithGoogle = () => {
    firebase.auth().signInWithPopup(googleProvider).catch(() => {
      setMessage('Sign-in failed. Please try again.')
    })
  }

  if (authLoading) return null

  return (
    <>
      <Helmet>
        <title>Edit Profile | ExplorOFF</title>
      </Helmet>

      <Header />

      <div className="exploroff-submit-page">
        <div className="container">
          <div className="exploroff-submit-card">
            <h1 className="exploroff-submit-title">Edit Your Profile</h1>
            <p className="exploroff-submit-subtitle">
              Tell the community who you are and what inspires your adventures.
            </p>

            {!user ? (
              <div className="exploroff-auth-section">
                <p className="exploroff-auth-prompt">Sign in to edit your profile.</p>
                <button type="button" className="exploroff-google-btn" onClick={signInWithGoogle}>
                  <svg className="exploroff-google-icon" viewBox="0 0 24 24" aria-hidden="true">
                    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z"/>
                    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                  </svg>
                  Sign in with Google
                </button>
              </div>
            ) : (
              <form onSubmit={handleSave} className="exploroff-submit-form">

                <div className="exploroff-profile-section-label">About you</div>

                <div className="exploroff-submit-group">
                  <label className="exploroff-submit-label" htmlFor="displayName">Your name</label>
                  <input
                    id="displayName"
                    className="exploroff-submit-input"
                    type="text"
                    name="displayName"
                    placeholder="Yulia Vasilyeva"
                    value={form.displayName}
                    onChange={handleChange}
                  />
                </div>

                <div className="exploroff-submit-group">
                  <label className="exploroff-submit-label" htmlFor="bio">Bio</label>
                  <textarea
                    id="bio"
                    className="exploroff-submit-textarea"
                    name="bio"
                    placeholder="Tell us about yourself — what drives your wanderlust?"
                    value={form.bio}
                    onChange={handleChange}
                  />
                </div>

                <div className="exploroff-profile-section-label">Travel personality</div>

                <div className="exploroff-submit-group">
                  <label className="exploroff-submit-label" htmlFor="favoriteDestination">
                    What's your favorite destination so far?
                  </label>
                  <input
                    id="favoriteDestination"
                    className="exploroff-submit-input"
                    type="text"
                    name="favoriteDestination"
                    placeholder="Iceland, Patagonia, Moab..."
                    value={form.favoriteDestination}
                    onChange={handleChange}
                  />
                </div>

                <div className="exploroff-submit-group">
                  <label className="exploroff-submit-label" htmlFor="dreamDestination">
                    Where do you dream of going next?
                  </label>
                  <input
                    id="dreamDestination"
                    className="exploroff-submit-input"
                    type="text"
                    name="dreamDestination"
                    placeholder="New Zealand, Faroe Islands..."
                    value={form.dreamDestination}
                    onChange={handleChange}
                  />
                </div>


                <div className="exploroff-submit-group">
                  <label className="exploroff-submit-label" htmlFor="favoriteActivity">
                    Favorite outdoor activity
                  </label>
                  <input
                    id="favoriteActivity"
                    className="exploroff-submit-input"
                    type="text"
                    name="favoriteActivity"
                    placeholder="Hiking, kayaking, mountain biking..."
                    value={form.favoriteActivity}
                    onChange={handleChange}
                  />
                </div>

                <div className="exploroff-profile-section-label">Do you offer a travel experience?</div>

                <div className="exploroff-submit-group exploroff-agent-toggle-wrap">
                  <label className="exploroff-agent-toggle-label">
                    <input
                      type="checkbox"
                      name="isAgent"
                      className="exploroff-agent-checkbox"
                      checked={form.isAgent}
                      onChange={handleChange}
                    />
                    <span className="exploroff-agent-toggle-text">
                      I'm a travel agent, tour guide, or Airbnb experience host
                    </span>
                  </label>
                </div>

                {form.isAgent && (
                  <div className="exploroff-agent-fields">
                    <div className="exploroff-submit-group">
                      <label className="exploroff-submit-label" htmlFor="businessName">Name of your experience or business</label>
                      <input
                        id="businessName"
                        className="exploroff-submit-input"
                        type="text"
                        name="businessName"
                        placeholder="Seattle Summit Hikes, Adventure Escapes Co..."
                        value={form.businessName}
                        onChange={handleChange}
                      />
                    </div>

                    <div className="exploroff-submit-group">
                      <label className="exploroff-submit-label" htmlFor="businessTagline">One-line description</label>
                      <input
                        id="businessTagline"
                        className="exploroff-submit-input"
                        type="text"
                        name="businessTagline"
                        placeholder="Guided hikes for all levels in the PNW."
                        value={form.businessTagline}
                        onChange={handleChange}
                      />
                    </div>

                    <div className="exploroff-submit-group">
                      <label className="exploroff-submit-label" htmlFor="businessSpecialty">What do you offer?</label>
                      <input
                        id="businessSpecialty"
                        className="exploroff-submit-input"
                        type="text"
                        name="businessSpecialty"
                        placeholder="Guided hikes, Airbnb experience, kayak tours..."
                        value={form.businessSpecialty}
                        onChange={handleChange}
                      />
                    </div>

                    <div className="exploroff-submit-group">
                      <label className="exploroff-submit-label" htmlFor="businessWebsite">Website</label>
                      <input
                        id="businessWebsite"
                        className="exploroff-submit-input"
                        type="url"
                        name="businessWebsite"
                        placeholder="https://airbnb.com/experiences/..."
                        value={form.businessWebsite}
                        onChange={handleChange}
                      />
                    </div>

                    <div className="exploroff-submit-group">
                      <label className="exploroff-submit-label" htmlFor="businessPhone">Instagram handle</label>
                      <input
                        id="businessPhone"
                        className="exploroff-submit-input"
                        type="text"
                        name="businessPhone"
                        placeholder="@yourhandle"
                        value={form.businessPhone}
                        onChange={handleChange}
                      />
                    </div>
                  </div>
                )}

                <div className="exploroff-submit-actions">
                  <button
                    type="submit"
                    className="exploroff-submit-button"
                    disabled={saving}
                  >
                    <span>{saving ? 'Saving…' : 'Save profile'}</span>
                  </button>
                </div>

                {message ? <p className="exploroff-submit-message">{message}</p> : null}

                <p className="itinerary-page-back" style={{ marginTop: 24 }}>
                  <Link to={`/user/${user.uid}`}>← Back to my profile</Link>
                </p>
              </form>
            )}
          </div>
        </div>
      </div>

      <SubscribeSection />
      <FooterSection />
    </>
  )
}
